import { Injectable, inject } from "@angular/core";
import { LorenBaseCrudService } from "../../../../shared/services/loren-base-crud-service/loren-base-crud.service";
import { LorenAssetEndpointsService } from "../loren-asset-endpoints-service/loren-asset-endpoints.service";
import { LorenAsset } from "../../interfaces/loren-asset.interface";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import {
  LorenAssetBinaryHistory,
  LorenAssetNumericHistory,
} from "../../interfaces/loren-asset-history.interface";
import { LorenAssetHistoryArg } from "./interfaces/loren-asset-history-arg.interface";
import { LorenImageSize } from "../../../../shared/services/loren-image-service/enums/loren-image-size.enum";
import { catchError, map, tap } from "rxjs/operators";
import { LorenImageService } from "../../../../shared/services/loren-image-service/loren-image.service";
import { LorenImage } from "../../../../shared/services/loren-image-service/interfaces/loren-image.interface";
import { LorenImageConstants } from "../../../../shared/services/loren-image-service/constants/loren-image.constants";
import { LorenAssetProperty } from "../../interfaces/loren-asset-property.interface";
import { LorenAssetPropertiesService } from "../loren-asset-properties-service/loren-asset-properties.service";
import { LorenGroupService } from "../../../loren-groups/services/loren-group-service/loren-group.service";
import { LorenDeviceService } from "../../../loren-devices/services/loren-device-service/loren-device.service";
import { LorenDevice } from "../../../loren-devices/interfaces/loren-device.interface";
import { LorenAssetTypeService } from "../loren-asset-type-service/loren-asset-type.service";
import { LorenAssetUtilsService } from "../loren-asset-utils-service/loren-asset-utils.service";
import { LorenHttpRequestOptions } from "../../../../shared/interfaces/loren-http-request-options.interface";
import { KeyValuePair } from "../../../../shared/interfaces/key-value-pair.interface";
import { GenericItems } from "../../../../shared/interfaces/generic-items.interface";
import { LorenMeteringType } from "../../interfaces/loren-metering-type";
import { LorenAssetTrackPointsRequest } from "../../interfaces/loren-asset-track-points-request.interface";
import { LorenAssetTrackPointsSummary } from "../../interfaces/loren-asset-track-points-summary.interface";
import { LorenLineChartNote } from "../../../../shared/modules/loren-line-chart/interfaces/loren-line-chart-note.interface";
import { GroupItemModel } from "../../../../shared/modules/loren-hierarchy-selector/group.interface";
import { PagedItems } from "../../../../shared/interfaces/paged-items.interface";
import { environment } from "../../../../../environments/environment";

/**
 * @deprecated
 */
@Injectable({
  providedIn: "root",
})
export class LorenAssetService extends LorenBaseCrudService<LorenAsset> {
  private endpoints: LorenAssetEndpointsService;
  private propertiesService = inject(LorenAssetPropertiesService);
  private groupService = inject(LorenGroupService);
  private lorenAssetTypeService = inject(LorenAssetTypeService);
  private lorenImageService = inject(LorenImageService);
  private deviceService = inject(LorenDeviceService);

  private updateAssetSubject: BehaviorSubject<Partial<LorenAsset>> =
    new BehaviorSubject<Partial<LorenAsset>>(null);

  private allCachedAssetsSubject =
    new BehaviorSubject<LorenAsset[]>(null);
  lastCachedAssets$: Observable<LorenAsset[]> =
    this.allCachedAssetsSubject.asObservable();

  constructor() {
    const endpoints = inject(LorenAssetEndpointsService);

    super(endpoints.assets());
  
    this.endpoints = endpoints;
  }

  getNumericHistory(arg: LorenAssetHistoryArg) {
    return this.http.get<LorenAssetNumericHistory>(`${environment.apiServerUrl}/assets/meterings/${arg.meteringId}/numeric-history`, {
      params: {
        aggregateTo: arg.aggregate,
        dateFrom: arg.from.toString(),
        dateTo: arg.to.toString(),
        sum: arg.sum,
      },
    });
  }

  trackingPointsSummary(trackPointsDTO: LorenAssetTrackPointsRequest) {
    return this.http.post<LorenAssetTrackPointsSummary>(
      this.endpoints.trackingPointsSummary(),
      trackPointsDTO,
      {}
    );
    // return of({countOfTrackPoints: 1000}).pipe(delay(1000));
  }

  getBinaryHistory(arg: LorenAssetHistoryArg) {
    return this.http.get<LorenAssetBinaryHistory>(this.endpoints.binaryHistory(arg), {});
  }

  broadcastAssetReload(asset: Partial<LorenAsset>) {
    this.updateAssetSubject.next(asset);
  }

  getAssetImageObservable(
    imageGuid: string,
    size: LorenImageSize = LorenImageSize.ORIGINAL
  ): Observable<string> {
    return this.lorenImageService.get(imageGuid, size).pipe(
      catchError(() =>
        of({
          content: LorenImageConstants.defaultImage,
        })
      ),
      map((x) => x.content)
    );
  }

  updateAssetImage(id: number, content: string) {
    return this.http.post<LorenImage>(this.endpoints.assetImage(id), {
      content,
    });
  }

  addAssetPhoto(
    id: number,
    content: { photo: string; comment: string; assetCondition: string }
  ) {
    return this.http.post(this.endpoints.assetPhoto(id), content);
  }

  getAssetPhotos(id: number) {
    return this.http.get(this.endpoints.assetPhoto(id), {});
  }

  getAllAssetsWithPropertiesAndGroups(): Observable<LorenAsset[]> {
    return forkJoin([
      this.query(),
      this.propertiesService.query(),
      this.groupService.query(),
      this.deviceService.query(),
      this.lorenAssetTypeService.query(),
    ]).pipe(
      map((res) => {
        const entities: LorenAsset[] = res[0].items;
        const properties: LorenAssetProperty[] = res[1].items;
        const groups: GroupItemModel[] = res[2].items;
        const devices: LorenDevice[] = res[3].items;

        entities.forEach((asset) => {
          const typeProperties = LorenAssetUtilsService.getAssetTypeProperties(
            res[4].items,
            asset
          );
          LorenAssetUtilsService.populateAssetProperties(
            properties,
            typeProperties,
            asset
          );

          asset.groups = groups
            .filter((g) => asset.belongsToGroups.indexOf(g.id) !== -1)
            .map((g) => g.name);
          asset.devicesData = devices.filter(
            (d) => asset.devices.indexOf(d.id) !== -1
          );
        });

        return this.sortAssetsByName(entities);
      })
    );
  }

  query(
    requestOptions?: LorenHttpRequestOptions,
    getParams?: KeyValuePair<string>[]
  ): Observable<GenericItems<LorenAsset>> {
    return super.query(requestOptions, getParams).pipe(
      map((x) => {
        x.items.forEach((a) => {
          this.prepareLocationProperty(a);
        });

        return {
          items: this.sortAssetsByName(x.items),
        };
      }),
      tap((x) => this.allCachedAssetsSubject.next(x.items))
    );
  }

  addNote(meteringId: number, note: LorenLineChartNote) {
    return this.http.post(this.endpoints.addNote(meteringId), note);
  }

  get(
    id: number,
    requestOptions?: LorenHttpRequestOptions,
    getParams?: KeyValuePair<string>[]
  ): Observable<LorenAsset> {
    return super.get(id, requestOptions, getParams).pipe(
      map((x) => {
        this.prepareLocationProperty(x);
        return x;
      })
    );
  }

  getPagedAssets(
    pageNum: number,
    pageSize: number,
    assets: number[],
    filterString: string = null
  ): Observable<PagedItems<LorenAsset>> {

    if (filterString && filterString.trim().length > 0) {
      return this.query().pipe(
        map((response) => {
          const filteredAssets = response.items.filter(asset => 
            asset.name.toLowerCase().includes(filterString.toLowerCase())
          );
          
          const startIndex = pageNum * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedAssets = filteredAssets.slice(startIndex, endIndex);
          
          return {
            items: paginatedAssets,
            total: filteredAssets.length,
            page: pageNum,
            size: pageSize
          };
        })
      );
    } else {
      const url = this.endpoints.pagedAssets(pageNum, pageSize, assets, filterString);
      return this.http.get<PagedItems<LorenAsset>>(url, {});
    }
  }

  getAllAssets() {
    return this.http.get<GenericItems<LorenAsset>>(this.endpoints.getAllAssets(), {});
  }

  getAnalysis(meteringIds: number[], start, end) {
    const url = `${this.endpoints.getAnalysis()}?start=${start}&end=${end}&ids=${meteringIds.join(
      ","
    )}`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(url, {});
  }

  private prepareLocationProperty(asset: LorenAsset) {
    const locationMetric = asset.metering?.find(
      (m) => m.type === LorenMeteringType.GPS_TRACKER_SENSOR
    );
    if (locationMetric) {
      const latitude = locationMetric.data.find((k) => k.key === "lat")?.value;
      const longitude = locationMetric.data.find((k) => k.key === "lng")?.value;
      if (latitude && longitude) {
        asset.location = {
          latitude: Number(latitude),
          longitude: Number(longitude),
        };
      } else {
        asset.location = asset.defaultLocation ?? {
          latitude: null,
          longitude: null,
        };
      }
    } else if (asset.defaultLocation) {
      asset.location = asset.defaultLocation;
    }
  }

  private sortAssetsByName(x: LorenAsset[]) {
    return x.sort((a, b) => {
      if (a?.name > b?.name) {
        return 1;
      }

      if (a?.name < b?.name) {
        return -1;
      }

      return 0;
    });
  }
}
